import React, { useState, useEffect } from 'react';
import { offerAPI, walletAPI } from '../../services/api';
import { ChartSkeleton } from '../../components/skeletons';

const S = {
  page: { padding: '24px 20px', background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 24 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 28 },
  statCard: { 
    padding: '24px 20px', 
    borderRadius: 14, 
    background: '#fff', 
    border: '1.5px solid var(--border)', 
    textAlign: 'center',
    boxShadow: 'var(--shadow-sm)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
  },
  statLabel: { fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 },
  statVal: { fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 700 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: 'var(--char)', marginBottom: 16, fontFamily: 'var(--font-serif)' },
  catRow: { 
    display: 'flex', 
    alignItems: 'center', 
    padding: '16px 20px', 
    borderRadius: 12, 
    background: '#fff', 
    border: '1.5px solid var(--border)', 
    marginBottom: 10,
    flexWrap: 'wrap',
    gap: 12,
    transition: 'all 0.2s ease'
  },
  barBg: { flex: 1, height: 10, borderRadius: 5, background: '#F3F4F6', minWidth: 140 },
  bar: (pct, color) => ({ height: 10, borderRadius: 5, background: color, width: `${Math.min(pct, 100)}%`, transition: 'width 0.3s' }),
  emptyBox: { textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 14, border: '1.5px solid var(--border)' },
};

export default function VendorAnalytics() {
  const [offers, setOffers] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      offerAPI.mine().then(r => setOffers(r.data.data || [])).catch(() => {}),
      walletAPI.me().then(r => setWallet(r.data.data || null)).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  // Aggregate this month
  const now = new Date();
  const leadsReceived = wallet?.transactions
    ? wallet.transactions.filter(t => {
        if (t.type !== 'debit' || !t.demandId) return false;
        const d = new Date(t.createdAt);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length
    : 0;

  const thisMonthOffers = offers.filter(o => {
    const d = new Date(o.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const responded = thisMonthOffers.filter(o => o.status !== 'pending').length;
  const accepted = thisMonthOffers.filter(o => o.status === 'accepted').length;

  const leadSpend = wallet?.transactions
    ? wallet.transactions
        .filter(t => {
          if (t.type !== 'debit' || !t.demandId) return false;
          const d = new Date(t.createdAt);
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        })
        .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0)
    : 0;

  const stats = [
    { label: 'Leads This Month', value: leadsReceived, color: 'var(--green)' },
    { label: 'Responded', value: responded, color: 'var(--char)' },
    { label: 'Accepted', value: accepted, color: 'var(--green)' },
    { label: 'Lead Spend', value: `\u20B9${leadSpend.toLocaleString('en-IN')}`, color: 'var(--gold-dk)' },
  ];

  // Performance by category
  const catMap = {};
  offers.forEach(o => {
    const cat = o.demand?.category?.name || o.demand?.category || 'Other';
    if (!catMap[cat]) catMap[cat] = { total: 0, accepted: 0 };
    catMap[cat].total++;
    if (o.status === 'accepted') catMap[cat].accepted++;
  });
  const catBreakdown = Object.entries(catMap).map(([name, d]) => ({
    name, total: d.total, accepted: d.accepted, rate: d.total ? Math.round((d.accepted / d.total) * 100) : 0,
  })).sort((a, b) => b.total - a.total);

  if (loading) {
    return <ChartSkeleton height={320} style={{ margin: '24px 20px' }} />;
  }

  return (
    <div style={S.page}>
      <div style={S.title}>Analytics</div>
      <div style={S.subtitle}>Your vendor performance overview</div>

      {/* Monthly Stats */}
      <div style={S.grid4}>
        {stats.map((s, i) => (
          <div key={i} className="card" style={S.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              e.currentTarget.style.borderColor = s.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            <div style={S.statLabel}>{s.label}</div>
            <div style={{ ...S.statVal, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Category Breakdown */}
      <div style={S.sectionTitle}>Performance by Category</div>
      {catBreakdown.length === 0 ? (
        <div style={S.emptyBox}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📊</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No data yet</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Start sending offers to see category performance</div>
        </div>
      ) : (
        catBreakdown.map((c, i) => (
          <div key={i} className="card" style={S.catRow}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = c.rate >= 50 ? '#1B5C3A' : c.rate >= 25 ? '#F5A623' : '#DC2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            <div style={{ width: 160, flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--char)' }}>{c.name}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, fontWeight: 500 }}>{c.total} leads · {c.accepted} won</div>
            </div>
            <div style={S.barBg}>
              <div style={S.bar(c.rate, c.rate >= 50 ? '#1B5C3A' : c.rate >= 25 ? '#F5A623' : '#DC2626')} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: c.rate >= 50 ? '#1B5C3A' : c.rate >= 25 ? '#F5A623' : '#DC2626', width: 54, textAlign: 'right', flexShrink: 0 }}>
              {c.rate}%
            </div>
          </div>
        ))
      )}
    </div>
  );
}
